import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Share2, Truck, RefreshCw, ShieldCheck, 
  ChevronLeft, ChevronRight, Minus, Plus, ShoppingBag,
  Check, Star, Ruler
} from 'lucide-react';
import { useShopify } from '../context/ShopifyContext';
import ProductCard from '../components/ProductCard';
import { toast } from '../hooks/use-toast';

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const slideIn = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0 }
};

// Loading skeleton
const ProductSkeleton = () => (
  <div className="min-h-screen bg-white pt-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12">
        <div className="animate-pulse">
          <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-pink-100 to-rose-100" />
          <div className="flex gap-2 mt-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="w-20 h-20 rounded-lg bg-pink-100" />
            ))}
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-pink-100 rounded w-3/4" />
          <div className="h-10 bg-pink-100 rounded w-1/3" />
          <div className="h-24 bg-pink-50 rounded" />
          <div className="h-12 bg-pink-100 rounded w-full" />
        </div>
      </div>
    </div>
  </div>
);

const ProductPage = () => {
  const { slug } = useParams();
  const { getProductByHandle, addToCart, products } = useShopify();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [imageZoom, setImageZoom] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const data = await getProductByHandle(slug);
        setProduct(data);
        
        // Set default variant
        if (data.variants?.length > 0) {
          const defaultVariant = data.variants.find(v => v.availableForSale) || data.variants[0];
          setSelectedVariant(defaultVariant);
          
          // Set default options
          const options = {};
          defaultVariant.selectedOptions?.forEach(opt => {
            options[opt.name] = opt.value;
          });
          setSelectedOptions(options);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug, getProductByHandle]);

  // Update variant when options change
  useEffect(() => {
    if (!product?.variants) return;

    const matchingVariant = product.variants.find(variant => {
      return variant.selectedOptions?.every(opt => 
        selectedOptions[opt.name] === opt.value
      );
    });

    if (matchingVariant) {
      setSelectedVariant(matchingVariant);
    }
  }, [selectedOptions, product]);

  const images = product?.images?.length > 0 
    ? product.images 
    : product?.featuredImage 
      ? [product.featuredImage] 
      : [{ url: 'https://placehold.co/600x800/fce7f3/be185d?text=No+Image' }];

  // Get unique options
  const optionTypes = {};
  product?.variants?.forEach(variant => {
    variant.selectedOptions?.forEach(opt => {
      if (!optionTypes[opt.name]) {
        optionTypes[opt.name] = new Set();
      }
      optionTypes[opt.name].add(opt.value);
    });
  });

  const handleOptionChange = (optionName, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value
    }));
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    
    setIsAddingToCart(true);
    try {
      await addToCart(product, selectedVariant);
      toast({
        title: "Added to cart!",
        description: `${product.title} has been added to your cart.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: product?.currencyCode || 'USD'
    }).format(price);
  };

  const hasDiscount = selectedVariant?.compareAtPrice && selectedVariant.compareAtPrice > selectedVariant.price;
  
  // Get related products
  const relatedProducts = products
    .filter(p => p.id !== product?.id && p.productType === product?.productType)
    .slice(0, 4);

  if (loading) return <ProductSkeleton />;

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <Link to="/collections/all" className="text-pink-600 hover:underline">
            Browse all products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-pink-50/50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.nav 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm"
          >
            <Link to="/" className="text-pink-600 hover:text-pink-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <Link to="/collections/all" className="text-pink-600 hover:text-pink-700">Shop</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">{product.title}</span>
          </motion.nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="space-y-4"
          >
            {/* Main Image */}
            <div 
              className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-pink-50 to-rose-50 cursor-zoom-in"
              onClick={() => setImageZoom(true)}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImageIndex}
                  src={images[currentImageIndex]?.url}
                  alt={images[currentImageIndex]?.altText || product.title}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </AnimatePresence>

              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
                  >
                    <ChevronLeft size={24} className="text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
                  >
                    <ChevronRight size={24} className="text-gray-700" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.isNew && (
                  <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full">
                    NEW
                  </span>
                )}
                {hasDiscount && (
                  <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-full">
                    SALE
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((image, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      currentImageIndex === index 
                        ? 'border-pink-500 shadow-lg' 
                        : 'border-transparent hover:border-pink-200'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.altText || `${product.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Details */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={slideIn}
            className="space-y-6"
          >
            {/* Title & Price */}
            <div>
              {product.vendor && (
                <p className="text-pink-600 font-medium mb-1">{product.vendor}</p>
              )}
              <h1 
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {product.title}
              </h1>
              
              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold text-pink-600">
                  {formatPrice(selectedVariant?.price || product.price)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-xl text-gray-400 line-through">
                      {formatPrice(selectedVariant.compareAtPrice)}
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-600 text-sm font-medium rounded">
                      Save {Math.round((1 - selectedVariant.price / selectedVariant.compareAtPrice) * 100)}%
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Availability */}
            <div className="flex items-center gap-2">
              {selectedVariant?.availableForSale ? (
                <>
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-600 font-medium">In Stock</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-red-600 font-medium">Out of Stock</span>
                </>
              )}
            </div>

            {/* Options */}
            {Object.entries(optionTypes).map(([optionName, values]) => (
              <div key={optionName} className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-gray-900">
                    {optionName}: <span className="font-normal text-gray-600">{selectedOptions[optionName]}</span>
                  </label>
                  {optionName.toLowerCase() === 'size' && (
                    <button 
                      onClick={() => setShowSizeGuide(true)}
                      className="text-pink-600 text-sm flex items-center gap-1 hover:underline"
                    >
                      <Ruler size={14} />
                      Size Guide
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {Array.from(values).map((value) => {
                    const isSelected = selectedOptions[optionName] === value;
                    // Check if this option combination is available
                    const isAvailable = product.variants.some(v => 
                      v.availableForSale && 
                      v.selectedOptions.some(opt => opt.name === optionName && opt.value === value)
                    );
                    
                    return (
                      <motion.button
                        key={value}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleOptionChange(optionName, value)}
                        disabled={!isAvailable}
                        className={`min-w-[3rem] px-4 py-2.5 rounded-xl border-2 font-medium transition-all ${
                          isSelected 
                            ? 'bg-pink-500 text-white border-pink-500 shadow-lg'
                            : isAvailable
                              ? 'bg-white text-gray-700 border-pink-200 hover:border-pink-400'
                              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through'
                        }`}
                      >
                        {value}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div className="space-y-3">
              <label className="font-semibold text-gray-900">Quantity</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border-2 border-pink-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    className="p-3 hover:bg-pink-50 transition-colors"
                  >
                    <Minus size={20} className="text-gray-600" />
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(prev => prev + 1)}
                    className="p-3 hover:bg-pink-50 transition-colors"
                  >
                    <Plus size={20} className="text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddToCart}
                disabled={!selectedVariant?.availableForSale || isAddingToCart}
                className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                  selectedVariant?.availableForSale
                    ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600 shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isAddingToCart ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    Adding...
                  </>
                ) : (
                  <>
                    <ShoppingBag size={20} />
                    {selectedVariant?.availableForSale ? 'Add to Cart' : 'Sold Out'}
                  </>
                )}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-4 border-2 border-pink-200 rounded-xl hover:bg-pink-50 transition-colors"
              >
                <Heart size={24} className="text-pink-500" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-4 border-2 border-pink-200 rounded-xl hover:bg-pink-50 transition-colors"
              >
                <Share2 size={24} className="text-pink-500" />
              </motion.button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-pink-100">
              {[
                { icon: Truck, text: 'Free Shipping' },
                { icon: RefreshCw, text: 'Easy Returns' },
                { icon: ShieldCheck, text: 'Secure Payment' }
              ].map((item, index) => (
                <div key={index} className="flex flex-col items-center text-center gap-2">
                  <div className="p-2 bg-pink-50 rounded-lg">
                    <item.icon size={20} className="text-pink-500" />
                  </div>
                  <span className="text-xs text-gray-600">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Description */}
            {product.description && (
              <div className="pt-6 border-t border-pink-100">
                <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
                {product.descriptionHtml ? (
                  <div 
                    className="prose prose-pink text-gray-600"
                    dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                  />
                ) : (
                  <p className="text-gray-600 leading-relaxed">{product.description}</p>
                )}
              </div>
            )}

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4">
                {product.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-pink-50 text-pink-600 text-sm rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct, index) => (
                <ProductCard 
                  key={relatedProduct.id} 
                  product={relatedProduct}
                  index={index}
                  onAddToCart={async (prod, variant) => {
                    try {
                      await addToCart(prod, variant);
                      toast({
                        title: "Added to cart!",
                        description: `${prod.title} has been added to your cart.`,
                      });
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "Failed to add item to cart.",
                        variant: "destructive"
                      });
                    }
                  }}
                />
              ))}
            </div>
          </motion.section>
        )}
      </div>

      {/* Image Zoom Modal */}
      <AnimatePresence>
        {imageZoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setImageZoom(false)}
          >
            <button 
              className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setImageZoom(false)}
            >
              <ChevronLeft size={24} className="text-white rotate-45" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={images[currentImageIndex]?.url}
              alt={product.title}
              className="max-w-full max-h-full object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Size Guide Modal */}
      <AnimatePresence>
        {showSizeGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowSizeGuide(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Size Guide</h3>
                <button onClick={() => setShowSizeGuide(false)} className="p-1 hover:bg-gray-100 rounded-full">
                  <ChevronLeft size={24} className="rotate-45" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-pink-100">
                      <th className="py-2 px-3 text-left">Size</th>
                      <th className="py-2 px-3 text-left">Bust</th>
                      <th className="py-2 px-3 text-left">Waist</th>
                      <th className="py-2 px-3 text-left">Hips</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { size: 'S', bust: '32-34"', waist: '24-26"', hips: '34-36"' },
                      { size: 'M', bust: '34-36"', waist: '26-28"', hips: '36-38"' },
                      { size: 'L', bust: '36-38"', waist: '28-30"', hips: '38-40"' },
                      { size: 'XL', bust: '38-40"', waist: '30-32"', hips: '40-42"' },
                    ].map((row) => (
                      <tr key={row.size} className="border-b border-pink-50">
                        <td className="py-2 px-3 font-medium">{row.size}</td>
                        <td className="py-2 px-3">{row.bust}</td>
                        <td className="py-2 px-3">{row.waist}</td>
                        <td className="py-2 px-3">{row.hips}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductPage;
